// All projects data from GitHub
export const allProjects = [
  {
    id: 10,
    slug: 'snuggr',
    title: 'Snuggr',
    subtitle: 'AI Super App for Berlin Pet Parents',
    description: 'A pre-launch pet super app — five tabs, one AI spine. ૮₍´｡• ᵕ •｡`₎ა  Walks, vets, food, sitters, and 2am "is-this-an-emergency?" worries all live in one cozy place where the AI does the orchestrating, so pet parents can focus on the floofs. ✿',
    fullDescription: `Snuggr is a pre-launch AI super app for Berlin pet parents. The thesis: pet parenting today is a tab-switching mess across five-to-ten disconnected services (vet portal, walker app, food subscription, insurance, training, emergency clinics). Snuggr collapses that into **five tabs and one AI spine** — and bets that *AI is not a feature inside the app, AI is the app.*

> ૮ ˶ᵔ ᵕ ᵔ˶ ა  *Pet parenting, orchestrated by AI.*

## The Insight

Pet parents in Berlin juggle five-to-ten disconnected services. The AI super app collapses that surface area: one mobile client, three backends streamed in, an AI spine that knows your pet, your routine, and your city.

## What's Inside the Deck

- **The Cast** — five personas, five revenue paths
- **Two Journeys** — onboarding warmth, triage urgency
- **The Brain** — one mobile client, three backends, streamed in
- **AI Surface Map** — six user-facing AI surfaces, one safety stack
- **Tier Routing** — three LLM tiers, one safety profile, cost down without compromising health queries
- **Safety-first Design** — 22 hardcoded patterns where the LLM is *never* invoked. Pet safety always trumps AI cleverness, encoded in code.
- **Honest Inventory** — a pre-launch deck with everything labeled "shipped" is a red flag, so this one isn't
- **Six Signature Decisions** — AI as navigation (not a tab), defer the paywall, and four more in one line each
- **Building With AI Agents** — multi-agent IDE stack, MCP integrations, custom skills + hooks, one PM
- **Business Model** — three revenue lines to reduce dependency
- **Roadmap + Reflection** — foundation done; polish + beta + launch in progress

## Three PM Decisions I'm Proud Of

1. **AI as navigation, not as a tab.** Putting an "AI" tab next to "Home" treats AI as a feature you visit. Snuggr makes AI the connective tissue between every tab — you don't *go to* the AI; the AI is *how you go anywhere*.
2. **Defer the paywall.** The crash on the paywall is the worst first impression for a pet-parent product. Trust before extraction.
3. **Hardcode emergency triage.** 22 patterns where the LLM is never invoked. Some answers are too important to gamble on a model temperature.

## Stack Highlights

- **AI:** multi-tier LLM routing (cost-aware, safety-aware), six user-facing AI surfaces over one safety stack
- **Architecture:** one mobile client, three backends, streamed
- **Dev workflow:** multi-agent IDE stack with MCP integrations and custom skills/hooks — one PM orchestrating
- **Business model:** three revenue lines (subscriptions, marketplace, partnerships)

## Status

Pre-launch. Foundation done. Polish + beta + launch in progress. Open the deck above for the full pitch — the orchestration thesis, the architecture, the safety stack, the decisions, and the year-one targets.

૮ ˶• ⤙ •˶ ა  *thanks for reading — go pet a dog* ✿`,
    image: '/images/snuggr.svg',
    technologies: ['Product Management', 'AI / LLMs', 'Multi-tier LLM Routing', 'MCP Integrations', 'Mobile (React Native)', 'Streaming Backends', 'Safety-first Design'],
    category: 'Product',
    liveUrl: '/decks/snuggr.html',
    featured: true,
    stats: {
      stage: 'Pre-launch',
      surface: '5 tabs · 1 AI spine',
      safety: '22 hardcoded patterns'
    }
  },
  {
    id: 9,
    slug: 'ohara-labs',
    title: 'OharaLabs',
    subtitle: 'AI-Orchestrated Learning Platform',
    description: 'Co-Founder & Product Lead. AI-orchestrated learning platform with a per-learner knowledge graph, Albert AI coach, capstone-gated retention, and SM-2 spaced repetition. Live with 2,000 users.',
    fullDescription: `Co-Founder + Product Lead at Ohara Labs — a live AI learning platform betting on **orchestration**, not content, as the new bottleneck in adult learning.

> "Coursera bet on better content. We bet on better orchestration."

## Thesis

Static content for an "average" learner has run its course. The senior engineer and the first-year student see the same video. Ohara replaces that with a per-learner knowledge graph and an AI coach (Albert) that orchestrates a coherent path from "I want to learn X" to "I shipped X."

## What's Live

- **Roadmap Builder** — conversational + async; returns a partial roadmap in under 30 seconds, then fills depth in the background via Trigger.dev workers
- **Albert** — streaming AI coach grounded in a persistent per-learner knowledge graph
- **Learning Studio** — minimalist Notion-style core; gamification reserved for habit edges
- **Capstones** — submit real work; Albert evaluates against rubrics, gating mastery
- **SM-2 spaced repetition** with mastery decay (nightly cron) + proactive resurfacing
- **Stripe tiered billing** — $18/mo Pro, aggressive free tier, AI quota + pay-as-you-go credits

## Real Metrics (live product, n=2,000)

- **2,000 users** live on oharalabs.tech
- **55.4% Day-30 retention** in the capstone-completer cohort (vs. 2–3% edtech baseline)
- **22% capstone completion** (~440 real submissions)
- **6% free→paid conversion** (2–3× edtech baseline)
- **65% reduction in drop-off during generation** (19.7% → 7%) after moving to async <30s roadmap delivery
- **+36% Day-30 lift** among gamification-active users

## Three PM Decisions I'm Proud Of

1. **Capstones over quizzes.** Mastery without proof is a self-report. Proof gates retention — and the data validated it: capstone completers retained 55.4% at Day 30.
2. **Async <30s, not synchronous spinner.** Latency is product. Move the wait off the critical path; let users start exploring while Albert builds in the background. Drop-off fell 65%.
3. **Notion-aesthetic core, gamification at the edges.** Adult professionals reject dopamine loops in their workspace. Synthesis: serious canvas, smart reinforcement (streaks, decay-resurfacing, capstone showcase).

## Stack

- **Background jobs / cron:** Trigger.dev (async roadmap generation, mastery decay)
- **Billing:** Stripe (tiered subscriptions + AI quota + pay-as-you-go credits)
- **AI:** Streaming LLM-grounded coach with rule-engine baseline (deterministic before AI)
- **Notifications:** Firebase Cloud Messaging
- **Algorithms:** SM-2 spaced repetition, knowledge-graph mastery scoring

## Reflections

- *Optimized for breadth before depth* — built for 8 personas in the PRD before validating depth on one. Should have shipped the engineer loop fully first, hit Day-30 targets, then expanded.
- *Built AI insights before deterministic ones* — users distrust intelligence they can't interrogate. Trust before novelty.
- *Underestimated the live-URL effect* — recruiter and investor conversations spiked the moment the URL was clickable. Demoability compounds before product-quality does.

**The full product portfolio deck is linked above.** It walks through the orchestration thesis, the architecture, three signature PM decisions with metrics, the competitive landscape, and the roadmap.`,
    image: '/images/ohara-labs.png',
    technologies: ['Product Management', 'AI / LLMs', 'Trigger.dev', 'Stripe', 'Firebase Cloud Messaging', 'SM-2 Spaced Repetition', 'Knowledge Graph'],
    category: 'Product',
    liveUrl: '/decks/ohara-labs.html',
    featured: true,
    stats: {
      users: '2,000 live users',
      retention: '55.4% Day-30',
      conversion: '6% free→paid'
    }
  },
  {
    id: 1,
    slug: 'agritech-ml',
    title: 'AgriTech ML',
    subtitle: 'Hyperspectral Image Classification',
    description: 'Hyperspectral Image Classification for precision agriculture. Uses satellite imagery and a CNN/Random Forest pipeline to classify crop types and monitor health.',
    fullDescription: `A comprehensive machine learning project revolutionizing precision agriculture through hyperspectral image analysis. This project processes 38,000+ satellite images to enable data-driven farming decisions.

## Project Overview

AgriTech ML uses advanced machine learning techniques to analyze hyperspectral satellite imagery, helping farmers optimize resource allocation and monitor crop health in real-time.

## Key Features

- **Hyperspectral Image Processing**: Analyzes 38,000+ satellite images with multiple spectral bands
- **Dual Model Approach**: Compares Random Forest and CNN architectures
- **High Accuracy**: Achieved 94% classification accuracy on test dataset
- **Resource Optimization**: Reduces computational cost by 30% through model optimization
- **Real-time Analysis**: Enables quick decision-making for farmers

## Technical Stack

- **Machine Learning**: TensorFlow, Scikit-Learn
- **Data Processing**: NumPy, Pandas
- **Visualization**: Matplotlib, Seaborn
- **Language**: Python, Jupyter Notebook

## Methodology

1. **Data Collection**: Gathered hyperspectral satellite imagery
2. **Preprocessing**: Normalized and cleaned 38K+ images
3. **Feature Engineering**: Extracted spectral features from multiple bands
4. **Model Development**: Built and compared Random Forest vs CNN
5. **Optimization**: Fine-tuned models for accuracy and performance
6. **Validation**: Tested on holdout dataset

## Results

- 94% classification accuracy
- 30% reduction in computational requirements
- Real-time crop health monitoring capability
- Scalable to different geographic regions

## Impact

This project has the potential to transform precision agriculture by:
- Reducing water and fertilizer waste
- Early detection of crop diseases
- Optimizing harvest timing
- Improving overall crop yields`,
    image: 'https://images.pexels.com/photos/34182341/pexels-photo-34182341.jpeg',
    technologies: ['Python', 'TensorFlow', 'Scikit-Learn', 'NumPy', 'Pandas', 'Jupyter Notebook'],
    category: 'Machine Learning',
    githubUrl: 'https://github.com/evilNami/AgriTech-ML',
    featured: true,
    stats: {
      dataPoints: '38,000+ images',
      accuracy: '94%',
      improvement: '30% faster'
    }
  },
  {
    id: 2,
    slug: 'us-accidents-severity-prediction',
    title: 'US Accidents Severity Prediction',
    subtitle: 'Big Data Pipeline on GCP',
    description: 'Analyzed 7.7M records using PySpark & HiveQL. Achieved 89.77% prediction accuracy for public safety policy-making.',
    fullDescription: `A comprehensive big data project analyzing 7.7 million traffic accident records to predict accident severity and support public safety policy decisions.

## Project Overview

Built a scalable big data pipeline on Google Cloud Platform to process massive accident datasets, extract insights, and develop predictive models for accident severity.

## Key Features

- **Big Data Processing**: Handled 7.7M+ accident records
- **Distributed Computing**: Leveraged PySpark for parallel processing
- **Data Warehouse**: Used HiveQL for efficient querying
- **Machine Learning**: Developed ensemble models for prediction
- **High Accuracy**: Achieved 89.77% prediction accuracy
- **Interactive Dashboard**: Created visualization for policy makers

## Technical Stack

- **Cloud Platform**: Google Cloud Platform (GCP)
- **Big Data**: PySpark, Apache Hive, HiveQL
- **Machine Learning**: MLlib, Scikit-Learn
- **Data Processing**: Python, Pandas
- **Visualization**: Tableau, Matplotlib

## Methodology

1. **Data Ingestion**: Loaded 7.7M records into GCP
2. **Data Cleaning**: Preprocessed using PySpark
3. **Feature Engineering**: Created temporal and spatial features
4. **Exploratory Analysis**: Identified key patterns using HiveQL
5. **Model Development**: Built ensemble ML models
6. **Evaluation**: Validated on test dataset
7. **Deployment**: Created interactive dashboard

## Results

- 89.77% prediction accuracy
- Identified top 5 factors contributing to severe accidents
- Reduced query time from minutes to seconds
- Enabled real-time severity predictions

## Impact

This project supports public safety initiatives by:
- Predicting accident severity in real-time
- Identifying high-risk locations and times
- Supporting evidence-based policy making
- Optimizing emergency response allocation`,
    image: 'https://images.pexels.com/photos/669612/pexels-photo-669612.jpeg',
    technologies: ['Python', 'PySpark', 'HiveQL', 'GCP', 'Machine Learning', 'Big Data'],
    category: 'Big Data',
    githubUrl: 'https://github.com/evilNami/US-Accidents-Severity-Prediction',
    featured: true,
    stats: {
      dataPoints: '7.7M records',
      accuracy: '89.77%',
      platform: 'GCP'
    }
  },
  {
    id: 3,
    slug: 'coinsight',
    title: 'CoinSight',
    subtitle: 'Crypto Analytics Platform',
    description: 'Reduced prediction error for Bitcoin/Ethereum trends using Rolling Averages and Volatility Indicators.',
    fullDescription: `An advanced cryptocurrency analytics platform leveraging multiple machine learning techniques to forecast Bitcoin and Ethereum price trends.

## Project Overview

CoinSight combines traditional statistical methods with modern deep learning approaches to predict cryptocurrency price movements with high accuracy.

## Key Features

- **Multi-Model Approach**: Random Forest, Ridge Regression, LSTM, Transformer
- **Feature Engineering**: Rolling Averages, Volatility Indicators, Technical Indicators
- **Real-time Predictions**: Live price trend forecasting
- **Reduced Error**: 25% improvement over baseline models
- **Interactive Dashboard**: User-friendly visualization

## Technical Stack

- **Machine Learning**: Scikit-Learn, TensorFlow, PyTorch
- **Deep Learning**: LSTM, Transformer models
- **Data Processing**: Pandas, NumPy
- **Visualization**: Plotly, Matplotlib
- **Language**: Python, Jupyter Notebook

## Methodology

1. **Data Collection**: Historical Bitcoin and Ethereum price data
2. **Feature Engineering**: Created rolling averages, volatility measures
3. **Model Development**: Implemented 4 different ML approaches
4. **Comparison**: Evaluated models on various metrics
5. **Ensemble**: Combined best models for final predictions
6. **Deployment**: Built interactive web dashboard

## Models Implemented

- **Random Forest**: Baseline ensemble method
- **Ridge Regression**: Linear model with regularization
- **LSTM**: Recurrent neural network for time series
- **Transformer**: Attention-based deep learning

## Results

- 25% reduction in RMSE compared to baseline
- Accurate short-term trend predictions
- Real-time price movement forecasting
- Identified key price movement indicators

## Impact

Helps cryptocurrency traders and investors:
- Make informed trading decisions
- Understand price volatility patterns
- Reduce investment risk
- Identify optimal entry/exit points`,
    image: 'https://images.pexels.com/photos/7873553/pexels-photo-7873553.jpeg',
    technologies: ['Python', 'TensorFlow', 'PyTorch', 'LSTM', 'Transformer', 'Pandas'],
    category: 'Predictive Analytics',
    githubUrl: 'https://github.com/evilNami/CoinSight',
    featured: true,
    stats: {
      models: '4 ML models',
      improvement: '25% RMSE reduction',
      assets: 'Bitcoin & Ethereum'
    }
  },
  {
    id: 4,
    slug: 'attrition-analysis-tableau',
    title: 'Attrition Analysis Dashboard',
    subtitle: 'Retention Solutions Using Tableau',
    description: 'Identified root causes of 16% turnover rate; delivered data-driven retention strategies to HR leadership.',
    fullDescription: `A comprehensive Tableau dashboard analyzing employee attrition patterns to develop data-driven retention strategies.

## Project Overview

Built an interactive Tableau dashboard analyzing employee data to identify key factors contributing to 16% turnover rate and recommend retention strategies.

## Key Features

- **Interactive Dashboard**: Built with Tableau for HR leadership
- **Root Cause Analysis**: Identified 5 key attrition factors
- **Demographic Insights**: Analyzed patterns across departments
- **Financial Impact**: Quantified cost of turnover
- **Actionable Recommendations**: Data-driven retention strategies

## Technical Stack

- **BI Tool**: Tableau
- **Data Processing**: SQL, Python
- **Statistical Analysis**: Pandas, NumPy
- **Database**: PostgreSQL

## Methodology

1. **Data Collection**: Gathered employee records and surveys
2. **Data Cleaning**: Preprocessed and validated data
3. **Exploratory Analysis**: Identified attrition patterns
4. **Statistical Testing**: Validated findings
5. **Dashboard Development**: Created interactive Tableau visualizations
6. **Recommendations**: Developed retention strategies

## Key Findings

- **Work-Life Balance**: #1 factor in attrition
- **Compensation**: Salary dissatisfaction in 32% of exits
- **Career Growth**: Lack of advancement opportunities
- **Department Variations**: Higher attrition in Sales and IT
- **Tenure Patterns**: Most exits within first 2 years

## Results

- Identified 5 key attrition drivers
- Projected 8% reduction in turnover with recommendations
- Estimated $500K annual savings from reduced turnover
- Improved HR decision-making speed by 60%

## Impact

Enabled HR leadership to:
- Focus retention efforts on high-risk groups
- Implement targeted intervention programs
- Improve compensation strategies
- Enhance career development opportunities`,
    image: 'https://images.pexels.com/photos/6694560/pexels-photo-6694560.jpeg',
    technologies: ['Tableau', 'SQL', 'Python', 'Statistical Analysis'],
    category: 'Business Intelligence',
    githubUrl: 'https://github.com/evilNami/Attrition-Analysis-and-Retention-Solutions-Using-Tableau',
    featured: true,
    stats: {
      turnoverRate: '16%',
      factors: '5 key drivers',
      projectedReduction: '8%'
    }
  },
  {
    id: 5,
    slug: 'power-bi-sales-intelligence',
    title: 'Power BI Sales Intelligence',
    subtitle: 'Strategic Sales Analytics',
    description: 'Comprehensive Sales Analytics and Forecasting solution built with Power BI. Transformed $1.1M in sales data into actionable insights.',
    fullDescription: `A comprehensive Sales Analytics and Forecasting solution built with Power BI, transforming $1.1M in sales data (2014-2017) into actionable insights for strategic decision-making.

## Project Overview

Developed an enterprise-grade Power BI dashboard analyzing 4 years of sales data to support strategic business decisions at GM Superstore.

## Key Features

- **Comprehensive Analytics**: Analyzed $1.1M in sales (2014-2017)
- **Sales Forecasting**: Predictive models for future revenue
- **Regional Analysis**: Performance breakdown by geography
- **Product Insights**: Category and segment profitability
- **Interactive Reports**: Drill-down capabilities

## Technical Stack

- **BI Tool**: Power BI
- **Data Processing**: Power Query, DAX
- **Database**: SQL Server
- **ETL**: Power BI Data Flows

## Methodology

1. **Data Integration**: Connected multiple data sources
2. **Data Modeling**: Created star schema
3. **DAX Calculations**: Built advanced measures
4. **Visualization**: Designed intuitive dashboards
5. **Forecasting**: Implemented time series predictions
6. **Testing**: Validated with stakeholders

## Dashboard Components

- **Sales Overview**: KPIs, trends, and comparisons
- **Regional Performance**: Geographic analysis
- **Product Analysis**: Category and segment breakdown
- **Customer Insights**: Segmentation and behavior
- **Forecasting**: Future revenue predictions

## Results

- Identified 15% year-over-year sales growth opportunity
- Discovered underperforming product categories
- Optimized regional sales strategies
- Reduced reporting time from days to minutes
- Enabled data-driven decision making

## Impact

Transformed business operations by:
- Providing real-time sales visibility
- Supporting strategic planning
- Identifying growth opportunities
- Optimizing inventory management`,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
    technologies: ['Power BI', 'DAX', 'SQL', 'Power Query'],
    category: 'Business Intelligence',
    githubUrl: 'https://github.com/evilNami/Power-BI-Implementation-for-Strategic-Sales-Intelligence',
    featured: true,
    stats: {
      salesVolume: '$1.1M',
      period: '2014-2017',
      growth: '15% opportunity'
    }
  },
  {
    id: 7,
    slug: 'connect-telecom',
    title: 'ConnectTelecom',
    subtitle: 'Database Management System',
    description: 'A robust and scalable database for a telecom provider, designed for efficient management of customers, services, billing, and network operations.',
    fullDescription: `A comprehensive database management system designed specifically for telecom providers, enabling efficient customer, service, billing, and network operations management.

## Project Overview

Built a scalable relational database system with strong data integrity constraints to handle complex telecom operations.

## Key Features

- **Customer Management**: Complete customer lifecycle tracking
- **Service Management**: Multiple service types and plans
- **Billing System**: Automated billing and payment processing
- **Network Operations**: Equipment and infrastructure tracking
- **Data Integrity**: Robust constraints and validation
- **Scalability**: Designed for millions of records

## Technical Stack

- **Database**: PostgreSQL / MySQL
- **Modeling**: ER Diagrams, Normalization (3NF)
- **Query Optimization**: Indexing, Query tuning
- **Language**: SQL, PL/pgSQL

## Database Design

1. **Entity-Relationship Modeling**: Designed comprehensive schema
2. **Normalization**: Achieved 3NF for data integrity
3. **Constraints**: Implemented foreign keys, checks, triggers
4. **Indexing**: Optimized for query performance
5. **Stored Procedures**: Business logic implementation
6. **Views**: Simplified complex queries

## Key Components

- **Customer Module**: Personal info, accounts, history
- **Service Module**: Plans, subscriptions, features
- **Billing Module**: Invoices, payments, credits
- **Network Module**: Infrastructure, maintenance, capacity
- **Support Module**: Tickets, resolutions, SLA tracking

## Results

- Sub-second query response for common operations
- Zero data integrity violations
- 99.9% uptime
- Supports concurrent users efficiently

## Impact

Improved telecom operations through:
- Streamlined customer onboarding
- Automated billing processes
- Real-time service provisioning
- Enhanced reporting capabilities`,
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800',
    technologies: ['PostgreSQL', 'SQL', 'Database Design', 'Data Modeling'],
    category: 'Database Engineering',
    githubUrl: 'https://github.com/evilNami/ConnectTelecom',
    featured: false,
    stats: {
      uptime: '99.9%',
      responseTime: '< 1 second',
      normalization: '3NF'
    }
  },
  {
    id: 8,
    slug: 'instagram-engagement-analysis',
    title: 'Instagram Engagement Pattern Analysis',
    subtitle: 'Social Media Analytics',
    description: 'Analysis of Instagram engagement patterns to identify optimal posting strategies and content types.',
    fullDescription: `A data analytics project analyzing Instagram engagement patterns to identify optimal content strategies and posting schedules.

## Project Overview

Analyzed Instagram engagement data to uncover patterns and provide actionable insights for content optimization.

## Key Features

- **Engagement Analysis**: Likes, comments, shares tracking
- **Temporal Patterns**: Best posting times identification
- **Content Analysis**: High-performing content types
- **Hashtag Optimization**: Effective hashtag strategies
- **Audience Insights**: Demographic analysis

## Technical Stack

- **Language**: Python, Jupyter Notebook
- **Libraries**: Pandas, NumPy, Matplotlib, Seaborn
- **Analysis**: Statistical testing, correlation analysis

## Methodology

1. **Data Collection**: Scraped Instagram metrics
2. **Data Cleaning**: Preprocessed engagement data
3. **Exploratory Analysis**: Identified patterns
4. **Statistical Testing**: Validated findings
5. **Visualization**: Created insights dashboards
6. **Recommendations**: Developed content strategy

## Key Findings

- Peak engagement times identified
- Optimal posting frequency determined
- High-performing content formats
- Hashtag effectiveness measured
- Audience behavior patterns

## Results

- Identified 3 optimal posting windows
- Discovered top 10 hashtags for reach
- Found content types with 2x engagement
- Mapped audience activity patterns

## Impact

Helped content creators:
- Optimize posting schedules
- Improve content strategy
- Increase engagement rates
- Grow follower base`,
    image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800',
    technologies: ['Python', 'Pandas', 'Matplotlib', 'Data Analysis'],
    category: 'Data Analytics',
    githubUrl: 'https://github.com/evilNami/Instagram-Engagement-Pattern-Analysis',
    featured: false,
    stats: {
      engagement: '2x improvement',
      postingTimes: '3 optimal windows',
      hashtags: 'Top 10 identified'
    }
  },
];

// Get featured projects
export const getFeaturedProjects = () => {
  return allProjects.filter(project => project.featured);
};

// Get project by slug
export const getProjectBySlug = (slug) => {
  return allProjects.find(project => project.slug === slug);
};

// Get projects by category
export const getProjectsByCategory = (category) => {
  return allProjects.filter(project => project.category === category);
};

// Get all categories
export const getAllCategories = () => {
  return [...new Set(allProjects.map(project => project.category))];
};